import { Injectable } from '@angular/core';
import { AbstractEntityService } from '@app/shared/abstract/abstract-entity.service';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ResourceData } from '@app/shared/abstract/resource.data';
import { ProductModel } from './product.model';
import { ProductVariantOptionModel } from './product-variant-option.model';

@Injectable({
  providedIn: 'root',
})
export class ProductVariantOptionService extends AbstractEntityService<ProductVariantOptionModel> {
  constructor(http: HttpClient) {
    super('/api/product-variant-options', http);
  }

  update(id: number, data: Partial<ProductVariantOptionModel>): Observable<ResourceData<ProductVariantOptionModel>> {
    return this.http.put<ResourceData<ProductVariantOptionModel>>(`${this.apiUrl}${this.basePath}/${id}`, data);
  }

  getUsage(optionId: number) {
    return this.http.get<{ events: Array<{ id: number; name: string; reference?: string | null; date: string | null }>; event_count: number }>(
      `${this.apiUrl}${this.basePath}/${optionId}/usage`,
    );
  }

  searchOptions(
    searchTerm: string,
    page: number = 1,
    pageSize: number = 50,
    filters?: { productCategories?: string[] }
  ): Observable<{ options: { value: any; label: string; meta?: any }[]; hasMore: boolean }> {
    const params: any = {
      pageNum: page,
      pageSize: pageSize,
    };

    if (searchTerm) {
      params['filter[search]'] = searchTerm;
    }
    if (filters?.productCategories && filters.productCategories.length > 0) {
      params['filter[product_categories]'] = filters.productCategories.join(',');
    }

    return this.http.get<any>(`${this.apiUrl}${this.basePath}`, { params }).pipe(
      map((response) => ({
        options: response.data.map((option: any) => {
          const optionCategory = option.category ? ` (${option.category})` : '';
          const productName = option.product_name ? ` — ${option.product_name}` : '';
          const variantName = option.variant_name ? ` / ${option.variant_name}` : '';

          return {
            value: option.id,
            label: `${option.name}${optionCategory}${variantName}${productName}`,
            meta: {
              category: option.category,
              price: option.price,
              product_name: option.product_name,
              variant_name: option.variant_name,
              option_category: option.category,
            },
          };
        }),
        hasMore: response.meta.current_page < response.meta.last_page,
      })),
    );
  }

  // Daily pricing methods
  getDailyPrices(optionId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}${this.basePath}/${optionId}/prices`);
  }

  getDailyPriceForDate(optionId: number, date: string): Observable<any> {
    return this.http.get(`${this.apiUrl}${this.basePath}/${optionId}/prices/${date}`);
  }

  storeDailyPrice(optionId: number, data: { date: string; price: number }): Observable<any> {
    return this.http.post(`${this.apiUrl}${this.basePath}/${optionId}/prices`, data);
  }

  storeBulkDailyPrices(optionId: number, data: { start_date: string; end_date: string; price: number }): Observable<any> {
    return this.http.post(`${this.apiUrl}${this.basePath}/${optionId}/prices/bulk`, data);
  }

  deleteBulkDailyPrices(optionId: number, data: { start_date: string; end_date: string }): Observable<any> {
    return this.http.request('delete', `${this.apiUrl}${this.basePath}/${optionId}/prices/bulk`, { body: data });
  }

  deleteDailyPrice(optionId: number, dailyPriceId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}${this.basePath}/${optionId}/prices/${dailyPriceId}`);
  }
}
